import React, { useState, useMemo } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

const Datatable = ({ 
  columns, 
  data, 
  className = '',
  actions = [],
  onAction = () => {},
  showActions = true
}) => {
  const [sortConfig, setSortConfig] = useState(null);
  const handleSort = (key) => {
    setSortConfig((current) => {
      if (!current || current.key !== key) {
        return { key, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      return null;
    });
  };

  const handleActionClick = (action, row) => {
    onAction(action, row);
  };

  const sortedData = useMemo(() => {
    if (!sortConfig || !data) return data;

    return [...data].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig]);

  return (
    <div className="w-full">
      <div className="overflow-x-auto relative rounded-lg shadow-sm">
        <div className="inline-block min-w-full align-middle">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    className={`px-4 sm:px-6 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider ${
                      column.sortable ? 'cursor-pointer hover:bg-gray-100' : ''
                    }`}
                    onClick={() => column.sortable && handleSort(column.key)}
                  >
                    <div className="flex items-center space-x-1">
                      <span className="whitespace-nowrap">{column.label}</span>
                      {column.sortable && (
                        <div className="flex flex-col">
                          <ChevronUp 
                            className={`h-3 w-3 ${
                              sortConfig?.key === column.key && sortConfig?.direction === 'asc'
                                ? 'text-[#FF6C6C]'
                                : 'text-gray-400'
                            }`}
                          />
                          <ChevronDown 
                            className={`h-3 w-3 -mt-1 ${
                              sortConfig?.key === column.key && sortConfig?.direction === 'desc'
                                ? 'text-[#FF6C6C]'
                                : 'text-gray-400'
                            }`}
                          />
                        </div>
                      )}
                    </div>
                  </th>
                ))}
                {showActions && actions.length > 0 && (
                  <th scope="col" className="px-4 py-3 text-xs font-medium tracking-wider text-right text-gray-500 uppercase sm:px-6">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedData && sortedData.map((row, rowIndex) => (
                <tr 
                  key={rowIndex}
                  className="transition-colors hover:bg-gray-50"
                >
                  {columns.map((column) => (
                    <td 
                      key={`${rowIndex}-${column.key}`} 
                      className="px-4 py-4 text-xs text-gray-500 sm:px-6"
                    >
                      <div className="whitespace-nowrap">
                        {column.render ? column.render(row[column.key], row) : row[column.key]}
                      </div>
                    </td>
                  ))}
                  {showActions && actions.length > 0 && (
                    <td className="px-4 py-4 text-sm text-gray-500 sm:px-6">
                      <div className="whitespace-nowrap">
                        {actions.map((action) => (
                          <button
                            key={action.key}
                            className="text-sm text-gray-500 hover:text-gray-900"
                            onClick={() => handleActionClick(action, row)}
                          >
                            {action.label}
                          </button>
                        ))}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="mt-2 text-sm text-center text-gray-500 md:hidden">
        Scroll horizontally to view more →
      </div>
    </div>
  );
};

export default Datatable;